import { useState, useEffect, useCallback, useRef } from 'react';
import { usePerformanceOptimizer } from './usePerformanceOptimizer';

interface HealthCheckResult {
  isHealthy: boolean;
  error: string | null;
  lastCheck: Date | null;
  responseTime: number | null;
}

// Singleton para gestionar health checks globalmente - OPTIMIZADO
class HealthCheckManager {
  private static instance: HealthCheckManager;
  private isChecking = false;
  private lastCheck: Date | null = null;
  private lastResult: HealthCheckResult = {
    isHealthy: true,
    error: null,
    lastCheck: null,
    responseTime: null
  };
  private subscribers: Set<(result: HealthCheckResult) => void> = new Set();
  private consecutiveFailures = 0;
  private maxFailures = 3;
  
  static getInstance() {
    if (!HealthCheckManager.instance) {
      HealthCheckManager.instance = new HealthCheckManager();
    }
    return HealthCheckManager.instance;
  }
  
  subscribe(callback: (result: HealthCheckResult) => void) {
    this.subscribers.add(callback);
    // Enviar resultado actual inmediatamente
    callback(this.lastResult);
    
    return () => {
      this.subscribers.delete(callback);
    };
  }
  
  private notifySubscribers(result: HealthCheckResult) {
    this.lastResult = result;
    this.subscribers.forEach(callback => {
      try {
        callback(result);
      } catch (error) {
        console.warn('🚨 Health check subscriber error:', error);
      }
    });
  }
  
  async performCheck(shouldPerformCheck: () => boolean, optimizedFetch: (url: string, options?: RequestInit) => Promise<Response>): Promise<HealthCheckResult> {
    // Evitar checks simultáneos
    if (this.isChecking) {
      return this.lastResult;
    }
    
    // Usar throttling inteligente
    if (!shouldPerformCheck()) {
      return this.lastResult;
    }
    
    // Evitar checks muy frecuentes (mínimo 60 segundos, más si hay fallos)
    const now = new Date();
    const minInterval = this.consecutiveFailures > 0 ? 120000 : 60000; // 2 minutos si hay fallos, 1 minuto normalmente
    
    if (this.lastCheck && (now.getTime() - this.lastCheck.getTime()) < minInterval) {
      return this.lastResult;
    }
    
    this.isChecking = true;
    this.lastCheck = now;
    
    const startTime = Date.now();
    
    try {
      // Health check optimizado - solo verificar que el servidor responde
      const response = await optimizedFetch(window.location.origin + '/', {
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      const responseTime = Date.now() - startTime;
      const isHealthy = response.ok;
      
      // Reset contador de fallos si está healthy
      if (isHealthy) {
        this.consecutiveFailures = 0;
      } else {
        this.consecutiveFailures = Math.min(this.consecutiveFailures + 1, this.maxFailures);
      }
      
      const result: HealthCheckResult = {
        isHealthy,
        error: isHealthy ? null : `HTTP ${response.status}`,
        lastCheck: now,
        responseTime
      };
      
      this.notifySubscribers(result);
      return result;
      
    } catch (error) {
      this.consecutiveFailures = Math.min(this.consecutiveFailures + 1, this.maxFailures);
      
      const result: HealthCheckResult = {
        isHealthy: false,
        error: error instanceof Error ? error.message : 'Health check failed',
        lastCheck: now,
        responseTime: Date.now() - startTime
      };
      
      this.notifySubscribers(result);
      return result;
    } finally {
      this.isChecking = false;
    }
  }
  
  getConsecutiveFailures() {
    return this.consecutiveFailures;
  }
}

export function useHealthCheck(intervalMs: number = 60000) { // Base: 60 segundos
  const [result, setResult] = useState<HealthCheckResult>({
    isHealthy: true,
    error: null,
    lastCheck: null,
    responseTime: null
  });
  const [isChecking, setIsChecking] = useState(false);
  const managerRef = useRef<HealthCheckManager>();
  
  // Usar optimizador de rendimiento
  const { shouldPerformHealthCheck, optimizedFetch, throttledLog } = usePerformanceOptimizer({
    debugMode: false, // Solo en desarrollo
    maxHealthChecksPerMinute: 1 // Máximo 1 health check por minuto
  });
  
  useEffect(() => {
    managerRef.current = HealthCheckManager.getInstance();
    
    throttledLog('log', '🔄 Health check initialized', { intervalMs });
    
    // Suscribirse a updates
    const unsubscribe = managerRef.current.subscribe((newResult) => {
      setResult(newResult);
      setIsChecking(false);
    });
    
    // Check inicial diferido para evitar sobrecarga en el arranque
    const initialCheckTimeout = setTimeout(() => {
      if (managerRef.current) {
        managerRef.current.performCheck(shouldPerformHealthCheck, optimizedFetch);
      }
    }, 5000); // Esperar 5 segundos antes del primer check
    
    // Interval para checks automáticos con backoff inteligente
    const interval = setInterval(() => {
      if (managerRef.current && !document.hidden) { // No hacer checks si la pestaña está oculta
        const failures = managerRef.current.getConsecutiveFailures();
        const dynamicInterval = Math.min(intervalMs * Math.pow(2, failures), 300000); // Máximo 5 minutos
        
        // Solo hacer check si ha pasado suficiente tiempo
        if (result.lastCheck && Date.now() - result.lastCheck.getTime() < dynamicInterval) {
          return;
        }
        
        setIsChecking(true);
        managerRef.current.performCheck(shouldPerformHealthCheck, optimizedFetch);
      }
    }, intervalMs);
    
    return () => {
      clearInterval(interval);
      clearTimeout(initialCheckTimeout);
      unsubscribe();
    };
  }, [intervalMs, shouldPerformHealthCheck, optimizedFetch, throttledLog]);
  
  const forceCheck = useCallback(() => {
    if (managerRef.current) {
      setIsChecking(true);
      // Forzar check ignorando throttling
      managerRef.current.performCheck(() => true, optimizedFetch);
    }
  }, [optimizedFetch]);
  
  return {
    ...result,
    isChecking,
    forceCheck,
    consecutiveFailures: managerRef.current?.getConsecutiveFailures() || 0
  };
}